import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { isMac } from '~/utils/platform';
import { isMobile } from '~/utils/mobile';
import type { UIMessage } from 'ai';
import { formatTokenCount, getRoughTokenSize, getTokenPercentage } from '~/utils/tokenCalculator';
import { useFeatureFlag } from '~/lib/hooks/posthog/useFeatureFlag';
import { TokenLimitBanner } from './TokenLimitBanner';
import PresetModal from './PresetModal';
import { useStore } from '@nanostores/react';
import {
  techStackSelectionStore,
  setFramework,
  setLanguage,
  type Framework,
  type Language,
} from '~/lib/stores/techStackSelection';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { frameworkService } from '~/lib/services/frameworkService';
import { rulesStore } from '~/lib/stores/rules';
import { projectId as projectIdStore } from '~/lib/persistence/useChatHistory';
import { apiSpecStore, clearApiSpecData } from '~/lib/stores/integrations';
import { PromptEnhancerButton, type EnhancerState } from './PromptEnhancerButton';
import { useRouteLoaderData } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';
import { themeStore } from '~/lib/stores/theme';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import type { ServerLoaderData } from '~/root';
import { useFigmaAndAttachments } from '@/lib/hooks/useFigmaAndAttachments';
import { ImageAttachmentPreview } from './ImageAttachmentPreview';
import { FigmaAttachmentPreview } from './FigmaAttachmentPreview';
import { AttachmentPreview } from './AttachmentPreview';
import { detachAllAssets, openAssetListPopup } from '~/lib/stores/assets';
import {
  ALLOWED_ASSET_IMAGE_EXTENSIONS,
  MAX_ATTACHED_IMAGES,
  MAX_ASSET_IMAGE_SIZE,
  MAX_PROJECT_ASSETS,
  tabViewEnabled,
} from '~/utils/constants';
import { projectAssetsStore } from '@/lib/stores/assets';
import type { ProjectAsset } from '@/lib/stores/assets';
import { canvasPendingAnnotationStore, consumePendingAnnotation } from '~/lib/stores/canvasElementSelection';
import { EditorState, Compartment } from '@codemirror/state';
import {
  EditorView,
  keymap,
  ViewUpdate,
  placeholder as cmPlaceholder,
  ViewPlugin,
  Decoration,
  type DecorationSet,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { createPillDecorator } from './CMDecorators';
import { PromptQuotaIndicator } from './PromptQuotaIndicator';
type AppType = 'web-app' | 'mobile-app' | 'landing-page';

const APP_TYPE_OPTIONS: { id: AppType; label: string; icon: string }[] = [
  { id: 'web-app', label: 'Full Stack App', icon: 'i-ph:globe-simple' },
  { id: 'mobile-app', label: 'Mobile App', icon: 'i-ph:device-mobile' },
  { id: 'landing-page', label: 'Landing Page', icon: 'i-ph:browser' },
];

const APP_TYPE_DEFAULT_FRAMEWORK: Record<AppType, { framework: Framework; language: Language }> = {
  'web-app': { framework: 'react_tailwind', language: 'ts' },
  'landing-page': { framework: 'html_tailwind', language: 'js' },
  'mobile-app': { framework: 'reactnative', language: 'ts' },
};

const DEFAULT_TAB_FRAMEWORKS = new Set<Framework>(['react_tailwind', 'html_tailwind', 'reactnative']);

const getAppTypeForFramework = (framework: Framework): AppType | null => {
  if (!DEFAULT_TAB_FRAMEWORKS.has(framework)) {
    return null;
  }

  if (framework === 'reactnative') {
    return 'mobile-app';
  }

  if (framework === 'html_css' || framework === 'html_tailwind') {
    return 'landing-page';
  }

  return 'web-app';
};

interface NewChatInputProps {
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  input?: string;
  status?: 'ready' | 'streaming' | 'submitted' | 'error' | 'importing' | 'fixing-errors' | null;
  chatStarted?: boolean;
  loading?: boolean;
  attachedImages?: string[] | null;
  showImageSuccessIcon?: boolean;
  showFigmaSuccessIcon?: boolean;
  showFigmaErrorIcon?: boolean;
  showSuccessIcon?: boolean;
  extractedDesign?: any;
  messages?: UIMessage[];
  isBlocked?: boolean;
  handleInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown?: (event: React.KeyboardEvent, messageInput?: string) => void;
  onAttachImage?: (imageData: string[]) => void;
  onSendMessage?: (event: React.UIEvent, messageInput?: string) => void;
  onStop?: () => void;
  setOauthIsOpen?: (isOpen: boolean) => void;
  setIsCodeIntegrationOpen?: (isOpen: boolean) => void;
  setShowRulesModal?: (isOpen: boolean) => void;
  setShowImageSuccessIcon?: (isOpen: boolean) => void;
  setShowFigmaSuccessIcon?: (isOpen: boolean) => void;
  setShowFigmaErrorIcon?: (isOpen: boolean) => void;
  setShowSuccessIcon?: (isOpen: boolean) => void;
  setResetOAuthModalState?: (reset: boolean) => void;
  setMessages?: (messages: any) => void;
  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
}

const TEXTAREA_MIN_HEIGHT = 90;
const TEXTAREA_MIN_HEIGHT_CHAT_STARTED = 75;

export const NewChatInput: React.FC<NewChatInputProps> = ({
  textareaRef,
  input,
  status = 'ready',
  chatStarted = false,
  loading = false,
  showSuccessIcon = false,
  messages,
  isBlocked = false,
  handleInputChange,
  handleKeyDown,
  onSendMessage,
  onStop,
  setIsCodeIntegrationOpen,
  setShowRulesModal,
  setShowSuccessIcon,
  setMessages,
  onBlur,
  onFocus,
}) => {
  // const { isFeatureEnabled } = useFeatureFlag();

  const { t } = useTranslation();
  const techStackSelection = useStore(techStackSelectionStore);
  const { projectRules } = useStore(rulesStore);
  const currentProjectId = useStore(projectIdStore);
  const userData = useStore(userStore);
  const rootData = useRouteLoaderData('root') as ServerLoaderData | undefined;
  const isFreePlan = !userData.isSubscribed;

  console.log('userData', userData);

  // API spec from global store
  const openApiSpec = useStore(apiSpecStore);

  const figma = useFigmaAndAttachments();

  const [imageTextAdded, setImageTextAdded] = useState(false);
  const [figmaTextAdded, setFigmaTextAdded] = useState(false);
  const [userHasClearedInput, setUserHasClearedInput] = useState(false);
  const prevModalOpen = useRef(figma.uiState.isModalOpen);
  const isControlled = typeof input === 'string' && typeof handleInputChange === 'function';
  const [localInput, setLocalInput] = useState('');
  const currentInput = isControlled ? input : localInput;
  const theme = useStore(themeStore);

  const isDisabled =
    status === 'streaming' || status === 'submitted' || loading || isBlocked || status === 'fixing-errors';
  const tokenPercentage = useMemo(() => getTokenPercentage(messages || []), [messages]);
  const isSendDisabled =
    (figma.hasFigmaError && !(status === 'streaming')) ||
    status === 'submitted' ||
    tokenPercentage >= 90 ||
    isBlocked ||
    (figma.attachedImages.length > 0 && figma.isAttachingImages) ||
    figma.areAssetsLoading;

  const applyInputValue = (nextValue: string) => {
    console.log('isControlled', isControlled);

    if (isControlled && handleInputChange) {
      const event = {
        target: { value: nextValue },
      } as unknown as React.ChangeEvent<HTMLTextAreaElement>;

      handleInputChange(event);
    } else {
      setLocalInput(nextValue);
    }
  };

  useEffect(() => {
    if (prevModalOpen.current && !figma.uiState.isModalOpen) {
      if (!figma.hasFigmaDesign) {
        setFigmaTextAdded(false);

        if (!imageTextAdded) {
          setUserHasClearedInput(false);
        }
      }
    }

    prevModalOpen.current = figma.uiState.isModalOpen;
  }, [figma.uiState.isModalOpen, figma.hasFigmaDesign, imageTextAdded]);

  // inject text for figma
  useEffect(() => {
    if (figma.hasFigmaDesign) {
      if (currentInput === '' && !figmaTextAdded && !userHasClearedInput) {
        const figmaMessage =
          "I've connected a Figma design with the entire metadata. Please implement this in code with the help of the context provided.";
        applyInputValue(figmaMessage);
        setFigmaTextAdded(true);
      }
    } else if (!figma.hasFigmaDesign) {
      setFigmaTextAdded(false);
      setUserHasClearedInput(false);
    }
  }, [
    figma.hasFigmaDesign,
    currentInput,
    figmaTextAdded,
    userHasClearedInput,
    handleInputChange,
    figma.attachedImages.length,
  ]);

  // Inject text for Images
  useEffect(() => {
    if (figma.attachedImages.length > 0 && !figma.hasFigmaDesign) {
      if (currentInput === '' && !imageTextAdded && !userHasClearedInput) {
        const imageMessage = "I've attached image(s). Please implement this in code.";
        applyInputValue(imageMessage);
        setImageTextAdded(true);
      }
    } else if (figma.attachedImages.length === 0) {
      setImageTextAdded(false);
      setUserHasClearedInput(false);
    }
  }, [
    figma.attachedImages.length,
    figma.hasFigmaDesign,
    currentInput,
    imageTextAdded,
    userHasClearedInput,
    handleInputChange,
  ]);

  // Remove attachment message text when all assets are removed
  useEffect(() => {
    const imageMessage = "I've attached image(s). Please implement this in code.";

    // Check if all assets are removed (no figma, no images, no referenced assets)
    const hasNoAssets = !figma.hasFigmaDesign && figma.attachedImages.length === 0 && !figma.hasAssets;

    if (hasNoAssets && currentInput) {
      let newInput = currentInput;
      let inputChanged = false;

      // Remove image message if present
      if (currentInput.includes(imageMessage)) {
        newInput = newInput.replace(imageMessage, '').trim();
        inputChanged = true;
        setImageTextAdded(false);
      }

      // Remove figma message if present
      if (newInput.includes(imageMessage)) {
        newInput = newInput.replace(imageMessage, '').trim();
        inputChanged = true;
        setFigmaTextAdded(false);
      }

      // Only update if the input actually changed
      if (inputChanged) {
        applyInputValue(newInput);
        setUserHasClearedInput(false);
      }
    }
  }, [figma.hasFigmaDesign, figma.attachedImages.length, figma.hasAssets, currentInput, handleInputChange]);

  const onTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (event.target.value === '' && (figmaTextAdded || imageTextAdded)) {
      setUserHasClearedInput(true);
    }

    // console.log('[onTextAreaChange] isControlled', isControlled);

    if (event?.target?.value?.length > 50000) {
      alert('Input is too large. Please reduce the input size.');
      return;
    }

    if (isControlled) {
      handleInputChange?.(event);
    } else {
      setLocalInput(event.target.value);
    }
  };

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalIsTopUp, setUpgradeModalIsTopUp] = useState(false);
  const [hasSelectedStack, setHasSelectedStack] = useState(false);
  const [showStackReminder, setShowStackReminder] = useState(false);
  const [enhancerState, setEnhancerState] = useState<EnhancerState>('default');
  const [originalPrompt, setOriginalPrompt] = useState<string>('');
  const [enhancedPrompt, setEnhancedPrompt] = useState<string>('');
  const [selectedAppType, setSelectedAppType] = useState<AppType | null>(() => {
    const { framework } = selectionStore.get();
    return getAppTypeForFramework(framework);
  });
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const assetUploadInputRef = useRef<HTMLInputElement>(null);
  const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;

  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorViewRef = useRef<EditorView | null>(null);
  const latestCallbacks = useRef<any>({});

  const monthlyPromptsUsed = parseInt(userData.promptsUsed, 10) || 0;
  const maxPromptsMonthly = parseInt(userData.maxPrompts, 10) || 0;
  const topUpPromptsUsed = parseInt(userData.topUpPromptsUsed, 10) || 0;
  const topUpPrompts = parseInt(userData.topUpPrompts, 10) || 0;
  const promptsUsedNum = monthlyPromptsUsed + topUpPromptsUsed;
  const maxPromptsNum = maxPromptsMonthly + topUpPrompts;
  const isLaunchPlan = (userData.planName || '').toLowerCase().includes('launch');
  const isProPlan = (userData.planName || '').toLowerCase().includes('pro');

  console.log('isLaunchPlan', isLaunchPlan, 'isProPlan', isProPlan);
  useEffect(() => {
    latestCallbacks.current = {
      handlePaste,
      handleKeyDownWithValidation,
      onTextAreaChange,
      applyInputValue,
      currentInput,
      onBlur,
      onFocus,
      onPillRemove: handlePillRemove,
    };
  });

  const referencedAssetsCount = useStore(projectAssetsStore).filter((asset) => asset.isReferenced).length;
  const pendingAnnotation = useStore(canvasPendingAnnotationStore);

  useEffect(() => {
    if (!pendingAnnotation) {
      return;
    }

    let text = consumePendingAnnotation();

    if (!text) {
      return;
    }

    const separator = currentInput.length > 0 ? ' ' : '';
    text = text.replace(/\r?\n/g, ' ');

    const instructionMarker = '| Instruction:';
    const instructionIndex = text.indexOf(instructionMarker);

    let cleanedContext = text;
    let instructionPart = '';

    if (instructionIndex !== -1) {
      cleanedContext = text.substring(0, instructionIndex).trim();
      instructionPart = text.substring(instructionIndex + instructionMarker.length).trim();
    }

    const selectedElementMarker = 'Selected element:';
    const selectedElementIndex = cleanedContext.indexOf(selectedElementMarker);

    if (selectedElementIndex !== -1) {
      cleanedContext = cleanedContext.substring(selectedElementIndex + selectedElementMarker.length).trim();
    }

    // Wrap the annotation context in an asset pill and append the instruction outside
    const annotationString = `[[annotation:${cleanedContext}]]`;
    const finalInsertion = instructionPart ? `${annotationString} ${instructionPart}` : annotationString;

    applyInputValue(`${currentInput}${separator}${finalInsertion} `);
  }, [pendingAnnotation]);

  // Helper function to format tech stack display
  const getTechStackDisplay = () => {
    if (!hasSelectedStack) {
      return 'Select Stack';
    }

    const { framework, language } = techStackSelection;
    const frameworkName = frameworkService.formatFramework(framework);
    const languageName = language.toUpperCase();

    return `${frameworkName} + ${languageName}`;
  };

  // Synchronize techStackSelectionStore with selectionStore on mount
  useEffect(() => {
    const { framework, language } = selectionStore.get();
    setFramework(framework);
    setLanguage(language);
  }, []);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(isMobile());
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const { framework, language } = techStackSelection;

    if (framework && language) {
      setHasSelectedStack(true);
    }
  }, [techStackSelection]);

  // --- CodeMirror Setup ---
  useEffect(() => {
    let view: EditorView | null = null;

    if (editorContainerRef.current) {
      const placeholderText = !chatStarted ? t('chat.placeholder') : t('chat.placeholderChatStarted');
      const customTheme = EditorView.theme(
        {
          '&': {
            backgroundColor: 'transparent',
            color: 'var(--dualite-text-primary)',
            fontSize: '1rem', // text-md
            caretColor: 'var(--dualite-cursor-color) !important',
          },
          '.cm-cursor, .cm-dropCursor': {
            borderLeft: '1.5px solid var(--dualite-cursor-color) !important',
          },
          '&.cm-focused': { outline: 'none' },
          '.cm-scroller': {
            fontFamily: 'inherit',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--dualite-background-tertiary) var(--dualite-background-tertiary)',
            minHeight: !chatStarted ? `${TEXTAREA_MIN_HEIGHT}px` : `${TEXTAREA_MIN_HEIGHT_CHAT_STARTED}px`,
            maxHeight: `${TEXTAREA_MAX_HEIGHT}px`,
          },
          '.cm-content': {
            paddingTop: '1rem',
            paddingBottom: '0.75rem',
            paddingLeft: isMobileView ? '0.75rem' : '1rem',
            paddingRight: isMobileView ? '0.75rem' : '1rem',
          },
          '&.cm-disabled .cm-content': {
            cursor: 'not-allowed',
          },
          '.cm-placeholder': {
            color: 'var(--alpha-elements-textSecondary)',
          },
        },
        {
          dark: theme === 'dark',
        },
      );

      const handleDOMEvents = EditorView.domEventHandlers({
        paste(event) {
          if (latestCallbacks.current.handlePaste) {
            latestCallbacks.current.handlePaste(event as unknown as React.ClipboardEvent);
          }

          return false;
        },
        blur(event) {
          if (latestCallbacks.current.onBlur) {
            latestCallbacks.current.onBlur(event);
          }

          return false;
        },
        focus(event) {
          if (latestCallbacks.current.onFocus) {
            latestCallbacks.current.onFocus(event);
          }

          return false;
        },
      });

      // Compartments for dynamic configuration
      const readOnlyCompartment = new Compartment();
      const attributesCompartment = new Compartment();

      // Pill Plugin
      const pillDecorator = createPillDecorator(latestCallbacks);

      const pillPlugin = ViewPlugin.fromClass(
        class {
          decorations: DecorationSet;

          constructor(view: EditorView) {
            this.decorations = pillDecorator.createDeco(view);
          }

          update(update: ViewUpdate) {
            this.decorations = pillDecorator.updateDeco(update, this.decorations);
          }
        },
        {
          decorations: (v) => v.decorations,
          provide: (plugin) =>
            EditorView.atomicRanges.of((view) => {
              return view.plugin(plugin)?.decorations || Decoration.none;
            }),
        },
      );

      const state = EditorState.create({
        doc: latestCallbacks.current.currentInput || '',
        extensions: [
          history(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          EditorView.lineWrapping,
          cmPlaceholder(placeholderText),
          customTheme,
          handleDOMEvents,
          pillPlugin,
          EditorView.updateListener.of((update: ViewUpdate) => {
            if (update.docChanged && update.view.hasFocus) {
              const nextValue = update.state.doc.toString();

              if (latestCallbacks.current.onTextAreaChange) {
                const syntheticEvent = {
                  target: { value: nextValue },
                } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
                latestCallbacks.current.onTextAreaChange(syntheticEvent);
              }
            }
          }),
          readOnlyCompartment.of(EditorState.readOnly.of(isDisabled || false)),
          attributesCompartment.of(EditorView.editorAttributes.of(isDisabled ? { class: 'cm-disabled' } : {})),
          EditorView.contentAttributes.of({ 'aria-label': t('chat.inputLabel') }),
        ],
      });

      view = new EditorView({
        state,
        parent: editorContainerRef.current,
      });

      // Store compartment for dynamic updates
      (view as any)._compartments = { readOnly: readOnlyCompartment, attributes: attributesCompartment };

      editorViewRef.current = view;
    }

    return () => {
      view?.destroy();
      editorViewRef.current = null;
    };
  }, [chatStarted, isMobileView, t, TEXTAREA_MAX_HEIGHT, theme]);

  // Synchronize read-only state without re-creating the editor
  useEffect(() => {
    const view = editorViewRef.current;
    const compartments = (view as any)?._compartments;

    if (view && compartments?.readOnly && compartments?.attributes) {
      view.dispatch({
        effects: [
          compartments.readOnly.reconfigure(EditorState.readOnly.of(isDisabled || false)),
          compartments.attributes.reconfigure(
            EditorView.editorAttributes.of(isDisabled ? { class: 'cm-disabled' } : {}),
          ),
        ],
      });
    }
  }, [isDisabled]);

  // Synchronize external changes into CodeMirror
  useEffect(() => {
    const view = editorViewRef.current;

    if (view && currentInput !== undefined && view.state.doc.toString() !== currentInput) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: currentInput || '' },
      });
    }
  }, [currentInput]);

  // Proxy the parent textarea ref
  useEffect(() => {
    if (textareaRef && typeof textareaRef !== 'function') {
      const mockTextarea = {
        blur: () => editorViewRef.current?.contentDOM.blur(),
        focus: () => {
          const view = editorViewRef.current;

          if (view && !view.hasFocus) {
            view.focus();
          }
        },
        get value() {
          if (editorViewRef.current) {
            return editorViewRef.current.state.doc.toString();
          }

          return latestCallbacks.current.currentInput || '';
        },
        set value(val: string) {
          if (editorViewRef.current) {
            editorViewRef.current.dispatch({
              changes: { from: 0, to: editorViewRef.current.state.doc.length, insert: val },
            });

            if (latestCallbacks.current.applyInputValue) {
              latestCallbacks.current.applyInputValue(val);
            }
          }
        },
        get scrollHeight() {
          return editorViewRef.current?.scrollDOM.scrollHeight ?? 0;
        },
        get scrollTop() {
          return editorViewRef.current?.scrollDOM.scrollTop ?? 0;
        },
        set scrollTop(val: number) {
          if (editorViewRef.current) {
            editorViewRef.current.scrollDOM.scrollTop = val;
          }
        },
      } as unknown as HTMLTextAreaElement;

      // @ts-ignore: Intentionally bypassing readonly to support legacy textarea parent refs
      textareaRef.current = mockTextarea;
    }
  }, [textareaRef]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }

      if (attachMenuRef.current && !attachMenuRef.current.contains(event.target as Node)) {
        setShowAttachMenu(false);
      }
    };

    if (showMoreMenu || showAttachMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu, showAttachMenu]);

  // Clear stack reminder after animation
  useEffect(() => {
    if (showStackReminder) {
      const timer = setTimeout(() => {
        setShowStackReminder(false);
      }, 1000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [showStackReminder]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      toast.error('No file selected. Please choose an image to upload.');
      return;
    }

    // Block file upload when Figma design is attached
    if (figma.hasFigmaDesign) {
      toast.error('Please remove the Figma design first to attach images.');
      event.target.value = '';
      return;
    }

    // Check count using hook helper
    if (!figma.canAttachImages && files.length > 0) {
      // Just let hook handle validation on attachImages, but we can pre-check
      const currentCount = figma.nonFigmaImages.length;

      if (currentCount >= MAX_ATTACHED_IMAGES) {
        toast.error('You can only attach up to 2 images at a time. Please remove some images first.');
        event.target.value = '';
        return;
      }
    }

    const validImages: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!ALLOWED_ASSET_IMAGE_EXTENSIONS.some((ext) => file.name.endsWith(ext))) {
        toast.error('This file format is not supported.');
        event.target.value = '';
        return;
      }

      try {
        const base64Image = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        validImages.push(base64Image);
      } catch (error) {
        console.error('Error processing image:', error);
        toast.error(`Failed to process image "${file.name}". Please try again.`);
        event.target.value = '';
        return;
      }
    }

    if (validImages.length > 0) {
      figma.attachImages(validImages, 'upload');
    }

    event.target.value = '';
  };

  const handlePaste = async (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items;

    if (!items) {
      return;
    }

    const imageFiles: File[] = [];

    // Use standard for loop for FileList/DataTransferItemList compatibility
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();

        if (file) {
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length === 0) {
      return;
    }

    // Block pasting images when Figma design is attached
    if (figma.hasFigmaDesign) {
      event?.preventDefault();
      toast.error('Please remove the Figma design first to attach images.');
      return;
    }

    const base64Images: string[] = [];

    try {
      for (const file of imageFiles) {
        const base64Image = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        base64Images.push(base64Image);
      }

      if (base64Images.length > 0) {
        figma.attachImages(base64Images, 'paste');
      }
    } catch (error) {
      console.error('Error processing pasted images:', error);
      toast.error('Failed to process pasted images. Please try again.');
    }
  };

  // Update enhancer state when disabled prop changes or input changes
  useEffect(() => {
    // Disable if textarea is disabled or input is empty
    const shouldDisable = isDisabled || !currentInput || currentInput.trim().length === 0;

    if (shouldDisable && enhancerState !== 'disabled') {
      setEnhancerState('disabled');
    } else if (!shouldDisable && enhancerState === 'disabled') {
      setEnhancerState('default');
    }
  }, [isDisabled, enhancerState, currentInput]);

  const handlePillRemove = (pillText: string) => {
    const currentVal = editorViewRef.current?.state.doc.toString() || '';
    const newVal = currentVal.replace(pillText, '').trim();

    const syntheticEvent = {
      target: { value: newVal },
    } as unknown as React.ChangeEvent<HTMLTextAreaElement>;

    if (latestCallbacks.current.onTextAreaChange) {
      latestCallbacks.current.onTextAreaChange(syntheticEvent);
    }
  };

  // Wrap handleKeyDown to validate stack selection
  const handleKeyDownWithValidation = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      if (isSendDisabled) {
        event.preventDefault();
        event.stopPropagation();

        if (figma.areAssetsLoading || (figma.attachedImages.length > 0 && figma.isAttachingImages)) {
          toast.info('Please wait until all images/assets finish processing before sending your message');
        }

        return true;
      }

      if (!chatStarted && !hasSelectedStack) {
        event.preventDefault();
        event.stopPropagation();

        setShowStackReminder(true);
        toast.info('Please select a tech stack to get started');
        return true;
      }

      event.preventDefault(); // Stop default Enter behavior
      event.stopPropagation(); // Stop CodeMirror from making a new line

      handleKeyDown?.(event, currentInput);

      if (!isControlled) {
        setLocalInput('');
        setUserHasClearedInput(false);
      }

      return true;
    }

    handleKeyDown?.(event, currentInput);
    return false;
  };

  // Prompt Enhancer handlers
  const handleEnhance = async () => {
    if (!currentInput || currentInput.trim().length === 0) {
      toast.error('Please enter a prompt first');
      return;
    }

    // Store the original prompt
    setOriginalPrompt(currentInput);
    setEnhancerState('generating');

    try {
      const { framework, language, packageManager } = techStackSelection;

      // Prepare image/design data for the enhancer
      const requestBody: any = {
        message: currentInput,
        framework,
        language,
        packageManager,
        llmProvider: selectionStore.get().llmProvider,
        userEmail: userStore.get().email,
        projectId: projectIdStore.get(),
        planName: userStore.get().planName,
      };

      // Include attached images if present
      const { images: attachedImages, design: extractedDesign } = figma.getDesignForPrompt();

      if (attachedImages && attachedImages.length > 0) {
        requestBody.attachedImages = attachedImages;
      }

      // Include Figma design metadata if present
      if (extractedDesign) {
        const { image, colors, fonts, ...designMetadata } = extractedDesign;
        requestBody.extractedDesign = {
          hasImage: !!image,
          colors: colors || [],
          fonts: fonts || [],
          ...designMetadata,
        };
      }

      const response = await fetch('/api/promptenhancer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-dualite-token': localStorage.getItem('dualite_token') || '',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to enhance prompt');
      }

      const data = (await response.json()) as { enhancedPrompt?: string };

      if (data.enhancedPrompt) {
        setEnhancedPrompt(data.enhancedPrompt);

        // Update the textarea with the enhanced prompt
        if (textareaRef?.current) {
          textareaRef.current.value = data.enhancedPrompt;
          applyInputValue(data.enhancedPrompt);
        }

        setEnhancerState('success');
      } else {
        throw new Error('No enhanced prompt received');
      }
    } catch (error) {
      Sentry.captureException(`Error from handleEnhance in NewChatInput.client.tsx: ${error}`, {
        extra: {
          message: currentInput,
        },
      });
      Sentry.captureException(`Error from handleEnhance in NewChatInput.client.tsx: ${error}`, {
        extra: {
          message: currentInput,
        },
      });
      console.error('Error enhancing prompt:', error);
      toast.error('Failed to enhance prompt. Please try again.');
      setEnhancerState('default');

      // Restore original prompt on error
      if (textareaRef?.current && originalPrompt) {
        textareaRef.current.value = originalPrompt;
        applyInputValue(originalPrompt);
      }
    }
  };

  const handleEnhancerRedo = async () => {
    // Use the original prompt to regenerate
    if (!originalPrompt) {
      toast.error('No original prompt found');
      return;
    }

    setEnhancerState('generating');

    try {
      const { framework, language, packageManager } = techStackSelection;

      // Prepare image/design data for the enhancer
      const requestBody: any = {
        message: originalPrompt,
        framework,
        language,
        packageManager,
        llmProvider: selectionStore.get().llmProvider,
        userEmail: userStore.get().email,
        projectId: projectIdStore.get(),
        planName: userStore.get().planName,
      };

      const { images: attachedImages, design: extractedDesign } = figma.getDesignForPrompt();

      // Include attached images if present
      if (attachedImages && attachedImages.length > 0) {
        requestBody.attachedImages = attachedImages;
      }

      // Include Figma design metadata if present
      if (extractedDesign) {
        const { image, colors, fonts, ...designMetadata } = extractedDesign;
        requestBody.extractedDesign = {
          hasImage: !!image,
          colors: colors || [],
          fonts: fonts || [],
          ...designMetadata,
        };
      }

      const response = await fetch('/api/promptenhancer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-dualite-token': localStorage.getItem('dualite_token') || '',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to enhance prompt');
      }

      const data = (await response.json()) as { enhancedPrompt?: string };

      if (data.enhancedPrompt) {
        setEnhancedPrompt(data.enhancedPrompt);

        // Update the textarea with the new enhanced prompt

        if (textareaRef?.current) {
          textareaRef.current.value = data.enhancedPrompt;
          applyInputValue(data.enhancedPrompt);
        }

        setEnhancerState('success');
      } else {
        throw new Error('No enhanced prompt received');
      }
    } catch (error) {
      Sentry.captureException(`Error from handleEnhance in NewChatInput.client.tsx: ${error}`, {
        extra: {
          message: currentInput,
        },
      });
      console.error('Error enhancing prompt:', error);
      toast.error('Failed to enhance prompt. Please try again.');
      setEnhancerState('default');
    }
  };

  const handleEnhancerApply = () => {
    /*
     * Keep the enhanced prompt in the textarea
     * Just reset the state
     */
    setEnhancerState('default');
    setOriginalPrompt('');
    setEnhancedPrompt('');
    toast.success('Enhanced prompt applied!');
  };

  const handleEnhancerReject = () => {
    // Restore the original prompt
    if (originalPrompt && textareaRef?.current) {
      textareaRef.current.value = originalPrompt;
      applyInputValue(originalPrompt);
    }

    setEnhancerState('default');
    setOriginalPrompt('');
    setEnhancedPrompt('');
  };

  const handleAssetUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      toast.error('No file selected. Please choose a file to upload.');
      return;
    }

    const filesArray = Array.from(files);

    const invalidTypeFiles = filesArray.filter((file) => {
      const name = (file.name || '').toLowerCase();
      return !ALLOWED_ASSET_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
    });

    if (invalidTypeFiles.length > 0) {
      const fileNames = invalidTypeFiles.map((f) => f.name).join(', ');
      toast.error(`File "${fileNames}" is not an image. Please select only image files.`);
      event.target.value = '';
      return;
    }

    if (figma.assets.length + files.length > MAX_PROJECT_ASSETS) {
      toast.error(`You can only upload up to ${MAX_PROJECT_ASSETS} assets at a time. Please remove some assets first.`);
      event.target.value = '';
      return;
    }

    const oversizedFiles = filesArray.filter((file) => file.size > MAX_ASSET_IMAGE_SIZE);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ');
      toast.error(`The following files exceed the 5MB limit and cannot be uploaded: ${fileNames}`);
      event.target.value = '';
      return;
    }

    const success = await figma.uploadAssets(filesArray, currentProjectId || '');

    const latestAssets: ProjectAsset[] = projectAssetsStore.get();

    console.log('[assets] assets: ', latestAssets);

    const successfulUploads = latestAssets.filter((asset) => asset.status === 'ready');
    const failedUploads = latestAssets.filter((asset) => asset.status === 'error');

    if (success && successfulUploads.length > 0) {
      toast.success(`${successfulUploads.length} asset(s) uploaded successfully!`);
    } else {
      if (failedUploads.length > 0) {
        toast.error(`${failedUploads.length} asset(s) failed to upload. Please try again.`);
      }
    }

    // Reset input
    event.target.value = '';
  };

  return (
    <>
      <style>
        {`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-10px); }
            50% { transform: translateX(10px); }
            75% { transform: translateX(-10px); }
          }
          .hide-scroll::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
        `}
      </style>
      <div
        className={classNames('h-fit mx-auto z-prompt rounded-lg', {
          'w-full max-w-lg': chatStarted,
          'w-[90vw] sm:w-[85vw] md:w-[70vw] lg:w-[65vw] xl:w-[55vw] 2xl:w-[50vw]': !chatStarted,
        })}
      >
        {/* App Type Tab View - sits above the textbox */}
        {tabViewEnabled && !chatStarted && (
          <div className="flex items-center justify-between pb-2 pl-1 pr-1">
            <div
              className="flex items-center gap-1 overflow-x-auto hide-scroll pb-1"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {APP_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => {
                    setSelectedAppType(option.id);

                    const defaults = APP_TYPE_DEFAULT_FRAMEWORK[option.id];
                    setFramework(defaults.framework);
                    setLanguage(defaults.language);
                    selectionStore.setKey('framework', defaults.framework);
                    selectionStore.setKey('language', defaults.language);
                    setHasSelectedStack(true);
                  }}
                  className={classNames(
                    'flex items-center whitespace-nowrap shrink-0 gap-2 rounded-lg border px-3.5 py-1.5 text-sm font-medium transition-colors',
                    selectedAppType === option.id
                      ? 'bg-dualite-background-tertiary text-dualite-text-primary border-dualite-border-primary'
                      : 'border-transparent text-dualite-text-tertiary hover:text-dualite-text-secondary',
                  )}
                >
                  <div className={classNames(option.icon, 'text-sm')} />
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
            <div className="hidden sm:flex shrink-0 items-center gap-2">
              <button
                onClick={() => setShowPresetModal(true)}
                disabled={isDisabled}
                className={classNames(
                  'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  selectedAppType === null && hasSelectedStack
                    ? 'bg-dualite-background-tertiary text-dualite-text-primary border-dualite-border-primary'
                    : 'border-transparent text-dualite-text-tertiary hover:text-dualite-text-secondary',
                )}
              >
                <div className="i-ph:sliders-horizontal text-sm" />
                <span>
                  {selectedAppType === null && hasSelectedStack
                    ? frameworkService.formatFramework(techStackSelection.framework)
                    : 'Customise'}
                </span>
              </button>
              {isFreePlan && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  disabled={isDisabled}
                  className={classNames(
                    'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                  )}
                  style={{
                    background: '#233B22',
                    border: '1px solid #8DD29066',
                    color: '#8dd290',
                  }}
                >
                  <div className="i-ph:lightning-fill text-xs" />
                  <span>Upgrade</span>
                </button>
              )}
            </div>
          </div>
        )}

        <div
          className={classNames(
            'relative border border-[var(--dualite-border-primary)]',
            'bg-[var(--dualite-background-tertiary)] rounded-lg',
            'shadow-[var(--dualite-shadow-card)]',
          )}
        >
          {' '}
          {/* Attachment Previews - Above textarea */}
          {(figma.hasFigmaDesign || figma.nonFigmaImages.length > 0 || openApiSpec || figma.hasAssets) && (
            <div className="flex flex-row flex-wrap items-center gap-2 px-4 pt-4">
              {/* Figma File Preview */}
              {figma.hasFigmaDesign && !figma.hasFigmaError && (
                <FigmaAttachmentPreview
                  fileName={figma.designState.design?.name || 'Figma.File'}
                  onRemove={() => figma.detachDesign()}
                />
              )}

              {!figma.hasFigmaDesign && figma.nonFigmaImages.length > 0 && (
                <ImageAttachmentPreview images={figma.nonFigmaImages} onRemoveImage={(id) => figma.removeImage(id)} />
              )}

              {openApiSpec && (
                <AttachmentPreview
                  name="API Specification"
                  attachmentName="OpenAPI Spec"
                  onRemove={() => {
                    clearApiSpecData();
                    setShowSuccessIcon?.(false);
                  }}
                  icon={<div className="i-ph:code text-xl text-emerald-400" />}
                />
              )}

              {/* Asset Preview */}
              {figma.hasAssets && (
                <AttachmentPreview
                  name={referencedAssetsCount > 1 ? `${referencedAssetsCount} Attachments` : '1 Attachment'}
                  attachmentName="Click to view"
                  icon={<div className="i-ph:image text-xl text-emerald-400" />}
                  onRemove={async () => await detachAllAssets()}
                  loading={figma.areAssetsLoading}
                  onClickAttachmentName={() => openAssetListPopup()}
                />
              )}
            </div>
          )}
          {/* Prompt quota (Chat -> alert/cta) */}
          {chatStarted && userData.email !== 'user_email@dummy.dualite' && (
            <PromptQuotaIndicator
              used={promptsUsedNum}
              total={maxPromptsNum}
              isSubscribed={!!userData.isSubscribed}
              isProPlan={isProPlan}
              isLaunchPlan={isLaunchPlan}
              onOpenUpgrade={(mode) => {
                setUpgradeModalIsTopUp(mode === 'topup');
                setShowUpgradeModal(true);
              }}
            />
          )}
          {/* CodeMirror div */}
          <div
            ref={editorContainerRef}
            className="relative w-full text-md rounded-t-lg bg-[var(--dualite-background-tertiary)]"
            translate="no"
            onKeyDownCapture={handleKeyDownWithValidation}
          />
          {/* Token Limit Banner */}
          {chatStarted && messages && (
            <TokenLimitBanner
              className="mx-4 mt-3 mb-0"
              messages={messages}
              isStreaming={status === 'streaming'}
              setMessages={setMessages}
            />
          )}
          {/* Prompting suggestions for different input lengths */}
          {status !== 'streaming' &&
            status !== 'submitted' &&
            currentInput?.length > 25 &&
            currentInput?.length < 200 && (
              <a
                href="https://alphadocs.dualite.dev/prompt-techniques/prompt-techniques"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:text-gray-100 w-full flex items-center justify-end text-xs pr-4 pt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="i-ph:lightbulb text-md text-blue-300 w-8 -mr-1" />
                Add more details for better results. Click here for prompting tips.
              </a>
            )}
          {status !== 'streaming' && status !== 'submitted' && currentInput?.length >= 4000 && (
            <a
              href="https://alphadocs.dualite.dev/prompt-techniques/prompt-techniques"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-300 hover:text-gray-100 w-full flex items-center justify-end text-xs pr-4 pt-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="i-ph:lightbulb text-md text-amber-300 w-8 -mr-1 my-1" />
              Consider breaking down complex requests. Click here for prompting tips.
            </a>
          )}
          {/* Character count and keyboard shortcuts */}
          {currentInput?.length > 0 && (
            <div className="flex align-baseline justify-end gap-2 px-4 pb-2 text-xs">
              <div className="flex items-center gap-2">
                {currentInput?.length > 1 && (
                  <div className="text-[8px] leading-none text-dualite-text-tertiary flex items-center">
                    Use &nbsp; <kbd className="kdb bg-dualite-background-primary text-dualite-text-tertiary">Shift</kbd>{' '}
                    &nbsp;+&nbsp;
                    <kbd className="kdb bg-dualite-background-primary text-dualite-text-tertiary">
                      {isMac() ? 'Return' : 'Enter'}
                    </kbd>
                    &nbsp; for a new line
                  </div>
                )}
                {status !== 'streaming' && status !== 'submitted' && (
                  <div
                    className={`text-[8px] space-between flex flex-wrap items-center gap-x-4 leading-none ${
                      currentInput?.length >= 4000
                        ? 'text-amber-400'
                        : currentInput?.length > 25 && currentInput?.length < 200
                          ? 'text-blue-400'
                          : 'text-dualite-text-tertiary'
                    }`}
                  >
                    {currentInput?.length}/50000 (Max)
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Bottom Bar with Buttons */}
          <div
            className={classNames('flex items-center justify-between gap-3 pt-2 pb-5', isMobileView ? 'px-2' : 'px-4')}
          >
            {' '}
            {/* Left side buttons */}
            <div
              className={classNames('flex items-center', isMobileView ? 'gap-1.5' : 'gap-2.5', {
                'w-full': enhancerState === 'generating' || enhancerState === 'success',
              })}
            >
              {/* Only show upload button when enhancer is not in active state */}
              {enhancerState !== 'success' && enhancerState !== 'generating' && (
                <>
                  {/* Upload Button */}
                  <div className="relative" ref={attachMenuRef}>
                    <input
                      type="file"
                      id="new-image-upload"
                      accept={ALLOWED_ASSET_IMAGE_EXTENSIONS.join(',')}
                      multiple
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={isDisabled}
                    />
                    <input
                      type="file"
                      id="asset-upload"
                      ref={assetUploadInputRef}
                      accept={ALLOWED_ASSET_IMAGE_EXTENSIONS.join(',')}
                      multiple
                      className="hidden"
                      onChange={handleAssetUpload}
                      disabled={isDisabled || figma.areAssetsLoading}
                    />
                    <button
                      onClick={() => setShowAttachMenu(!showAttachMenu)}
                      disabled={isDisabled}
                      className={classNames(
                        'flex items-center justify-center w-8 h-8 rounded-lg transition-all',
                        'bg-dualite-background-button hover:bg-dualite-background-buttonHover',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                      )}
                      title={t('chat.attachImages')}
                    >
                      <div className="i-ph:plus text-lg text-dualite-text-primary" />
                    </button>

                    {/* Attach Menu Popup */}
                    {showAttachMenu && (
                      <div
                        className={classNames(
                          'absolute z-50 left-0 w-56 bg-dualite-background-button rounded-lg shadow-lg border border-dualite-border-primary overflow-hidden z-50',
                          chatStarted ? 'bottom-full mb-2' : 'top-full mt-2',
                        )}
                      >
                        <button
                          onClick={() => {
                            setShowAttachMenu(false);

                            if (figma.hasFigmaDesign) {
                              toast.error('Please remove the Figma design first to attach images.');
                              return;
                            }

                            if (!figma.canAttachImages) {
                              toast.error(
                                'You can only attach up to 2 images at a time. Please remove some images first.',
                              );
                              return;
                            }

                            document.getElementById('new-image-upload')?.click();
                          }}
                          disabled={figma.hasFigmaDesign}
                          className={classNames(
                            'w-full px-2 py-3 text-left text-sm text-dualite-text-primary transition-colors flex items-center gap-3',
                            figma.hasFigmaDesign || !figma.canAttachImages
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-dualite-background-buttonHover',
                          )}
                        >
                          <div className="i-ph:image-square text-lg" />
                          <span>{t('modals.attachments.referenceImage')}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (assetUploadInputRef.current) {
                              assetUploadInputRef.current.click();
                            }

                            setShowAttachMenu(false);
                          }}
                          disabled={isDisabled || figma.areAssetsLoading}
                          className={classNames(
                            'w-full px-2 py-3 text-left text-sm text-dualite-text-primary transition-colors flex items-center gap-3',
                            isDisabled || figma.areAssetsLoading
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-dualite-background-buttonHover',
                          )}
                        >
                          <div className="i-ph:upload-simple text-lg" />
                          <span>{t('modals.attachments.addAssets')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Prompt Enhancer Button */}
              <PromptEnhancerButton
                isMobile={isMobileView}
                disabled={isDisabled}
                state={enhancerState}
                onEnhance={handleEnhance}
                onRedo={handleEnhancerRedo}
                onApply={handleEnhancerApply}
                onReject={handleEnhancerReject}
                chatStarted={chatStarted}
              />

              {/* Only show other buttons when enhancer is not in active state */}
              {/* {enhancerState !== 'success' && enhancerState !== 'generating' && (
                <>
                  <button
                    onClick={() => figma.openModal()}
                    disabled={isDisabled || (figma.attachedImages.length > 0 && !figma.hasFigmaDesign)}
                    className={classNames(
                      'flex items-center justify-center gap-1.5 h-8 rounded-lg transition-all',
                      'bg-dualite-background-button hover:bg-dualite-background-buttonHover',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      figma.hasFigmaDesign && !figma.hasFigmaError ? 'ring-2 ring-green-500' : '',
                      figma.hasFigmaError ? 'ring-2 ring-red-500' : '',
                      isMobileView ? 'w-8' : 'px-2.5',
                    )}
                    title={
                      figma.hasFigmaDesign
                        ? t('chat.figmaDesignImported')
                        : figma.hasFigmaError
                          ? t('chat.figmaDesignTooLarge')
                          : t('chat.importFromFigma')
                    }
                  >
                    <FigmaIcon size={14} />
                    {!chatStarted && !isMobileView && <span className="text-sm text-dualite-text-primary">Figma</span>}
                  </button>
                </>
              )} */}
            </div>
            {/* Right side buttons */}
            <div className={classNames('flex items-center', isMobileView ? 'gap-1.5' : 'gap-2.5')}>
              {/* Only show right side buttons when enhancer is not in active state */}
              {enhancerState !== 'success' && enhancerState !== 'generating' && (
                <>
                  {/* Center - Tech Stack selector (only when not chatStarted) */}
                  {!tabViewEnabled && !chatStarted && (
                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowPresetModal(true)}
                        className={classNames(
                          'flex h-8 items-center gap-1.5 px-3 py-1.5 rounded-lg bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-buttonHover transition-all text-sm font-medium',
                          {
                            'opacity-50 disabled:cursor-not-allowed': isDisabled,
                            'border-2 border-red-500 animate-shake': showStackReminder,
                            'border border-transparent': !showStackReminder,
                          },
                        )}
                        disabled={isDisabled}
                        style={
                          showStackReminder
                            ? {
                                animation: 'shake 0.5s ease-in-out',
                              }
                            : undefined
                        }
                      >
                        <div className="i-ph:cube text-sm" />
                        {!isMobileView && <span>{getTechStackDisplay()}</span>}
                      </button>
                    </div>
                  )}

                  {/* More Button */}
                  <div className="relative" ref={moreMenuRef}>
                    <button
                      onClick={() => setShowMoreMenu(!showMoreMenu)}
                      disabled={isDisabled}
                      className={classNames(
                        'flex items-center justify-center h-8 rounded-lg transition-all gap-1.5',
                        'bg-dualite-background-button hover:bg-dualite-background-buttonHover',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isMobileView ? 'w-8' : 'px-2',
                      )}
                      title="More options"
                    >
                      <div className="i-ph:dots-three-circle text-lg text-dualite-text-primary" />
                      {!isMobileView && <span className="text-sm text-dualite-text-primary">More</span>}
                    </button>

                    {/* More Menu Popup */}
                    {showMoreMenu && (
                      <div
                        className={classNames(
                          'absolute right-0 w-56 bg-dualite-background-button rounded-lg shadow-lg border border-dualite-border-primary overflow-hidden z-50',
                          chatStarted ? 'bottom-full mb-2' : 'top-full mt-2',
                        )}
                      >
                        <button
                          onClick={() => {
                            setIsCodeIntegrationOpen?.(true);
                            setShowMoreMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left text-sm text-dualite-text-primary hover:bg-dualite-background-buttonHover transition-colors flex items-center gap-3"
                        >
                          <div className="i-ph:code text-lg" />
                          <span>{t('chat.attachAPISpec')}</span>
                          {showSuccessIcon && <div className="i-ph:check text-green-500 ml-auto" />}
                        </button>
                        <button
                          onClick={() => {
                            setShowRulesModal?.(true);
                            setShowMoreMenu(false);
                          }}
                          className="w-full px-4 py-3 text-left text-sm text-dualite-text-primary hover:bg-dualite-background-buttonHover transition-colors flex items-center gap-3"
                        >
                          <div className="i-ph:notebook text-lg" />
                          <span>{projectRules ? t('chat.editProjectRules') : t('chat.addProjectRules')}</span>
                          {projectRules && <div className="i-ph:check text-green-500 ml-auto" />}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Send Button */}
                  <button
                    onClick={(event) => {
                      console.log('Send button clicked with status:', status);

                      if (isSendDisabled) {
                        event?.preventDefault();

                        if (figma.areAssetsLoading) {
                          toast.info('Please wait until all assets finish processing before sending your message');
                        }

                        return;
                      }

                      // @mukul error or fixing-errors
                      if (status === 'error') {
                        toast.error('Please fix the errors first before sending your message');
                        event?.preventDefault();
                        return;
                      }

                      if (status === 'streaming' || status === 'fixing-errors') {
                        onStop?.();
                        return;
                      }

                      // Check if stack is selected (only when not chatStarted)
                      if (!chatStarted && !hasSelectedStack) {
                        event?.preventDefault();
                        setShowStackReminder(true);
                        toast.info('Please select a tech stack to get started');
                        return;
                      }

                      if (tokenPercentage >= 90) {
                        // Hard block sends at >=90%
                        event?.preventDefault();
                        toast.error(
                          'Cannot send message: approaching token limit. Please shorten your message or clear some chat history.',
                        );
                        return;
                      }

                      if (figma.hasFigmaError) {
                        toast.error('Please remove the invalid Figma design before sending');
                        return;
                      }

                      onSendMessage?.(event, currentInput);

                      if (!isControlled) {
                        setLocalInput('');
                        setUserHasClearedInput(false);
                      }
                    }}
                    disabled={isSendDisabled}
                    className={classNames(
                      'flex items-center justify-center gap-2 h-8 rounded-lg transition-all',
                      'bg-dualite-background-cta hover:bg-dualite-background-ctaHover shadow-sm text-dualite-text-cta',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      {
                        'hover:bg-red-400 hover:text-dualite-text-white':
                          status === 'streaming' || status === 'fixing-errors',
                        'px-3': (status === 'streaming' || status === 'fixing-errors') && !isMobileView,
                        'w-8': (status !== 'streaming' && status !== 'fixing-errors') || isMobileView,
                      },
                    )}
                    title={status === 'streaming' || status === 'fixing-errors' ? 'Stop' : 'Send'}
                  >
                    {status === 'submitted' || status === 'importing' ? (
                      <div className="i-svg-spinners:90-ring text-lg  " />
                    ) : status === 'streaming' || status === 'fixing-errors' ? (
                      <>
                        <div className="i-ph:stop-fill text-lg  " />
                        {!isMobileView && <span className="text-sm font-medium">Cancel</span>}
                      </>
                    ) : (
                      <div className="i-ph:arrow-up text-lg" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Prompt quota (Home pre-chat -> thin banner under input) */}
        </div>
        {!chatStarted && isFreePlan && userData.email !== 'user_email@dummy.dualite' && (
          <PromptQuotaIndicator
            variant="prechat"
            used={promptsUsedNum}
            total={maxPromptsNum}
            isSubscribed={!!userData.isSubscribed}
            isProPlan={isProPlan}
            isLaunchPlan={isLaunchPlan}
            onOpenUpgrade={() => {
              setUpgradeModalIsTopUp(false);
              setShowUpgradeModal(true);
            }}
          />
        )}
        {/* Preset Modal */}
        <PresetModal
          isOpen={showPresetModal}
          onClose={() => setShowPresetModal(false)}
          onUpgrade={() => {
            setShowPresetModal(false);
            setUpgradeModalIsTopUp(false);
            setShowUpgradeModal(true);
          }}
          onContinue={(selectedPreset) => {
            console.log('Selected preset:', selectedPreset);
            setHasSelectedStack(true);

            const currentFramework = techStackSelectionStore.get().framework;
            setSelectedAppType(getAppTypeForFramework(currentFramework));
          }}
        />

        {/* Upgrade Modal */}
        <ProUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          isTopUp={upgradeModalIsTopUp}
          paymentUrl={rootData?.paymentUrl || ''}
        />
      </div>
    </>
  );
};
